import { createRouter, createWebHistory } from 'vue-router'
import { detectLocale, i18n, syncLocale } from '@/i18n'
import routes from '@route-config'

const VIDEO_SHOWCASE_PATH = '/video-showcase'
const VIDEO_SHOWCASE_URL = 'https://www.youtube.com/playlist?list=PLDzQAF1r5um0'

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

const STATIC_QA_PATHS = new Set(['/device-help-qa', '/digital-life-qa'])
const IGNORED_STATIC_QA_QUERY_KEYS = new Set(['env', 'mock', 'debug', 'appdebug'])

function isVideoShowcasePath(path) {
  return path === VIDEO_SHOWCASE_PATH || path === `${VIDEO_SHOWCASE_PATH}/`
}

function normalizeMalformedQueryValue(value) {
  if (Array.isArray(value)) {
    const restoredQuery = {}
    const normalizedValues = value.map((item) => {
      const [normalizedValue, restoredItemQuery] = normalizeMalformedQueryValue(item)
      Object.assign(restoredQuery, restoredItemQuery)
      return normalizedValue
    }).filter((item) => item !== undefined)

    return [normalizedValues, restoredQuery]
  }

  if (typeof value !== 'string' || !value.includes('?')) {
    return [value, {}]
  }

  const [normalizedValue, ...rawSegments] = value.split('?')
  const restoredQuery = {}

  for (const segment of rawSegments) {
    if (!segment) continue

    const params = new URLSearchParams(segment)

    for (const [key, item] of params.entries()) {
      restoredQuery[key] = item
    }
  }

  return [normalizedValue, restoredQuery]
}

function normalizeStaticQaQuery(query) {
  return Object.entries(query).reduce((acc, [key, value]) => {
    const [normalizedValue, restoredQuery] = normalizeMalformedQueryValue(value)

    acc[key] = normalizedValue
    Object.assign(acc, restoredQuery)

    return acc
  }, {})
}

function createSanitizedStaticQaQuery(to) {
  if (!STATIC_QA_PATHS.has(to.path)) return null

  const sanitizedQuery = normalizeStaticQaQuery(to.query)
  const hasNormalizedQuery = Object.keys(sanitizedQuery).some((key) => sanitizedQuery[key] !== to.query[key])
  let hasIgnoredQuery = false

  for (const key of IGNORED_STATIC_QA_QUERY_KEYS) {
    if (key in sanitizedQuery) {
      delete sanitizedQuery[key]
      hasIgnoredQuery = true
    }
  }

  return hasIgnoredQuery || hasNormalizedQuery ? sanitizedQuery : null
}

router.beforeEach((to, from, next) => {
  if (isVideoShowcasePath(to.path)) {
    window.location.replace(VIDEO_SHOWCASE_URL)
    next(false)
    return
  }

  const sanitizedQuery = createSanitizedStaticQaQuery(to)

  if (sanitizedQuery) {
    next({
      path: to.path,
      query: sanitizedQuery,
      hash: to.hash,
      replace: true
    })
    return
  }

  syncLocale(detectLocale(to.query, { defaultLocale: to.meta.defaultLocale }))
  document.title = i18n.global.t(to.meta.titleKey || 'routeTitles.default')
  next()
})

export default router
